import React, { useEffect, useState, Key, useMemo } from 'react';
import { Transfer, Row, Col, Button, message, Select } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import InternalControlLayout from 'components/Layouts/InternalControlLayout';
import ApiService from 'utils/ApiCalls';
import { Entity, EntityMap } from 'store/ducks/InternalControls/types';
import { RootState } from 'store/configureStore';
import { internalControlsOp } from 'store/ducks/InternalControls';
import Helpers from 'utils/Helpers';
import i18n from 'constants/translation/i18n';
import * as Styled from './Allocation.styled';

const { Option } = Select;

const Allocation: React.FC = () => {
  const dispatch = useDispatch();
  const { scheme } = useSelector((state: RootState) => ({
    scheme: state.global.scheme
  }));

  const [targets, setTargets] = useState<Record<string, Key[]>>({});
  const [initialTargets, setInitialTargets] = useState<Record<string, Key[]>>(
    {}
  );
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [selectedParents, setSelectedParents] = useState<
    Record<string, number>
  >({});
  const [groups, setGroups] = useState<Entity[]>([]);
  const [divisions, setDivisions] = useState<Entity[]>([]);
  const [regions, setRegions] = useState<Entity[]>([]);
  const [legalEntities, setLegalEntities] = useState<Entity[]>([]);
  const [businessUnits, setBusinessUnits] = useState<Entity[]>([]);

  const divisionMap = useMemo(
    () => new Map(divisions.map((d) => [d.id, d])),
    [divisions]
  );
  const regionMap = useMemo(
    () => new Map(regions.map((r) => [r.id, r])),
    [regions]
  );
  const legalEntityMap = useMemo(
    () => new Map(legalEntities.map((l) => [l.id, l])),
    [legalEntities]
  );
  const businessUnitMap = useMemo(
    () => new Map(businessUnits.map((b) => [b.id, b])),
    [businessUnits]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [g, d, r, le, bu] = await Promise.all([
          ApiService.getGroupBPR(),
          ApiService.getDivisionBPR(),
          ApiService.getRegionBPR(),
          ApiService.getLegalEntitiesBPR(),
          ApiService.getBusinessUnitBPR()
        ]);

        setGroups(g.data.data);
        setDivisions(d.data.data);
        setRegions(r.data.data);
        setLegalEntities(le.data.data);
        setBusinessUnits(bu.data.data);

        setInitialTargets({ '1': [], '2': [], '3': [], '4': [] });
      } catch {
        message.error('Failed to load allocation data');
      }
    };
    load();
  }, []);

  const buildAllocationPayload = <T extends Entity, K extends string>(
    keys: Key[],
    parentKey: K,
    parentId: number,
    entityMap: EntityMap<T>
  ) => ({
    data_list: keys.map((key) => {
      const id = Number(String(key).split('-')[1]);
      const entity = entityMap.get(id);
      if (!entity) throw new Error(`Entity ${id} not found`);
      return {
        id: entity.id,
        name: entity.name,
        [parentKey]: parentId
      } as T & Record<K, number>;
    })
  });

  const handleSave = async (id: string) => {
    const parentId = selectedParents[id];
    if (!parentId) {
      message.warning('Please select a parent first');
      return;
    }

    try {
      setLoading((p) => ({ ...p, [id]: true }));

      switch (id) {
        case '1':
          await ApiService.saveDivision(
            buildAllocationPayload(
              targets[id] || [],
              'group_id',
              parentId,
              divisionMap
            )
          );
          break;
        case '2':
          await ApiService.saveRegion(
            buildAllocationPayload(
              targets[id] || [],
              'division_id',
              parentId,
              regionMap
            )
          );
          break;
        case '3':
          await ApiService.saveLegalEntity(
            buildAllocationPayload(
              targets[id] || [],
              'region_id',
              parentId,
              legalEntityMap
            )
          );
          break;
        case '4':
          await ApiService.saveBusinessUnit(
            buildAllocationPayload(
              targets[id] || [],
              'legal_entity_id',
              parentId,
              businessUnitMap
            )
          );
          break;
      }

      setInitialTargets((p) => ({ ...p, [id]: targets[id] || [] }));
      dispatch(internalControlsOp.setHasChanges(false));
      message.success('Allocation saved successfully');
    } catch (e: any) {
      message.error(e?.message || 'Save failed');
    } finally {
      setLoading((p) => ({ ...p, [id]: false }));
    }
  };

  const configs = [
    {
      id: '1',
      title: i18n.t('ALLOCATION_DIVISIONS_GROUP'),
      parents: Helpers.toTransferItems(
        [...groups].sort((a, b) => a.name.localeCompare(b.name)),
        'group'
      ),
      data: Helpers.toTransferItems(
        [...divisions].sort((a, b) => a.name.localeCompare(b.name)),
        'division'
      )
    },
    {
      id: '2',
      title: i18n.t('ALLOCATION_REGIONS_DIVISION'),
      parents: Helpers.toTransferItems(
        [...divisions].sort((a, b) => a.name.localeCompare(b.name)),
        'division'
      ),
      data: Helpers.toTransferItems(
        [...regions].sort((a, b) => a.name.localeCompare(b.name)),
        'region'
      )
    },
    {
      id: '3',
      title: i18n.t('ALLOCATION_LEGAL_ENTITIES_REGION'),
      parents: Helpers.toTransferItems(
        [...regions].sort((a, b) => a.name.localeCompare(b.name)),
        'region'
      ),
      data: Helpers.toTransferItems(
        [...legalEntities].sort((a, b) => a.name.localeCompare(b.name)),
        'legal'
      )
    },
    {
      id: '4',
      title: i18n.t('ALLOCATION_BUSINESS_UNITS_LEGAL_ENTITY'),
      parents: Helpers.toTransferItems(
        [...legalEntities].sort((a, b) => a.name.localeCompare(b.name)),
        'legal'
      ),
      data: Helpers.toTransferItems(
        [...businessUnits].sort((a, b) => a.name.localeCompare(b.name)),
        'bu'
      )
    }
  ];

  // Update global hasChanges state when local changes occur
  useEffect(() => {
    const hasAnyChanges = configs.some((c) => {
      return (
        selectedParents[c.id] &&
        !Helpers.areKeysEqual(targets[c.id], initialTargets[c.id])
      );
    });
    dispatch(internalControlsOp.setHasChanges(hasAnyChanges));
  }, [targets, initialTargets, selectedParents, dispatch]);

  return (
    <InternalControlLayout>
      <Styled.ContentWrapper>
        <Row gutter={[24, 24]}>
          {configs.map((c) => {
            const hasChanges =
              selectedParents[c.id] &&
              !Helpers.areKeysEqual(targets[c.id], initialTargets[c.id]);

            return (
              <Col xs={24} sm={24} md={12} lg={12} xl={12} key={c.id}>
                <Styled.TransferCard
                  scheme={scheme}
                  title={c.title}
                  extra={
                    <Button
                      type='primary'
                      disabled={!hasChanges}
                      loading={loading[c.id]}
                      onClick={() => handleSave(c.id)}
                    >
                      {i18n.t('SAVE')}
                    </Button>
                  }
                >
                  <Select
                    placeholder='Select parent'
                    onChange={(v) => {
                      setSelectedParents((p) => ({ ...p, [c.id]: v }));
                      setTargets((p) => ({ ...p, [c.id]: [] }));
                      setInitialTargets((p) => ({ ...p, [c.id]: [] }));
                    }}
                  >
                    {c.parents.map((p) => (
                      <Option
                        key={p.key}
                        value={Number(String(p.key).split('-')[1])}
                      >
                        {p.title}
                      </Option>
                    ))}
                  </Select>

                  <Transfer
                    dataSource={c.data}
                    targetKeys={targets[c.id] || []}
                    onChange={(keys) =>
                      setTargets((p) => ({ ...p, [c.id]: keys }))
                    }
                    render={(item) => <span>{String(item.title || '')}</span>}
                    showSearch
                    disabled={!selectedParents[c.id]}
                    listStyle={{ width: '100%', maxWidth: 400, height: 280 }}
                    filterOption={(inputValue, option) => {
                      const title = String(option.title || '');
                      return title
                        .toLowerCase()
                        .includes(inputValue.toLowerCase());
                    }}
                  />
                </Styled.TransferCard>
              </Col>
            );
          })}
        </Row>
      </Styled.ContentWrapper>
    </InternalControlLayout>
  );
};

export default Allocation;
